import React, { useState, useEffect } from 'react';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Typography,
  Button,
  Space,
  Badge,
  Drawer,
} from 'antd';
import {
  DashboardOutlined,
  BarChartOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  MenuOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  ReloadOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useViewMode } from '../../hooks/useViewMode';
import { useFullscreen } from '../../contexts/FullscreenContext';

const { Header, Content } = Layout;
const { Text } = Typography;

interface DashboardLayoutProps {
  children: React.ReactNode;
  onRefresh?: () => void;
  refreshLoading?: boolean;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, onRefresh, refreshLoading }) => {
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [mouseAtTop, setMouseAtTop] = useState(false);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isMonitor } = useViewMode();
  const { isFullscreen, toggleFullscreen } = useFullscreen();

  // Handle mouse movement for fullscreen header auto-hide
  useEffect(() => {
    if (!isFullscreen) {
      setHeaderVisible(true);
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const isAtTop = e.clientY <= 60; // Show header when mouse is within 60px from top
      setMouseAtTop(isAtTop);
      setHeaderVisible(isAtTop);
    };

    const handleMouseLeave = () => {
      if (isFullscreen) {
        setHeaderVisible(false);
        setMouseAtTop(false);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    // Auto-hide header after 3 seconds in fullscreen
    const timer = setTimeout(() => {
      if (isFullscreen && !mouseAtTop) {
        setHeaderVisible(false);
      }
    }, 3000);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timer);
    };
  }, [isFullscreen, mouseAtTop]);

  // Show different menu items based on user role
  const getMenuItems = () => {
    const baseItems = [
      {
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
      },
    ];

    // Remove products and orders menu items
    const commonItems: Array<{
      key: string;
      icon: React.ReactNode;
      label: string;
    }> = [];

    // Analyst and above roles can see analytics
    const analyticsItems = ['analyst', 'sales', 'ceo', 'admin'].includes(user?.role || '') ? [
      {
        key: '/analytics',
        icon: <BarChartOutlined />,
        label: 'Analytics',
      },
    ] : [];

    // Admin can see user management
    const adminItems = user?.role === 'admin' ? [
      {
        key: '/users',
        icon: <UserOutlined />,
        label: 'Users',
      },
    ] : [];

    return [...baseItems, ...commonItems, ...analyticsItems, ...adminItems];
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    if (isMobile) {
      setMobileMenuVisible(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };



  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  const getRoleDisplayName = (role: string) => {
    const roleMap: { [key: string]: string } = {
      'ceo': 'CEO',
      'sales': 'Sales Director',
      'analyst': 'Data Analyst',
      'admin': 'System Administrator',
    };
    return roleMap[role] || role;
  };

  // Mobile menu
  const MobileMenu = () => (
    <Drawer
      title="Sales Dashboard"
      placement="left"
      onClose={() => setMobileMenuVisible(false)}
      open={mobileMenuVisible}
      bodyStyle={{ padding: 0 }}
    >
      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        items={getMenuItems()}
        onClick={handleMenuClick}
        style={{ border: 'none' }}
      />
    </Drawer>
  );

  // Monitor mode
  if (isMonitor) {
    return (
      <Layout className={`dashboard-layout monitor-mode ${isFullscreen ? 'fullscreen-mode' : ''}`}>
        {!isFullscreen && (
          <Header className="monitor-header">
            <div className="monitor-title">
              <Typography.Title level={3} style={{ margin: 0, color: '#fff' }}>
                Sales Dashboard - Monitor Center
              </Typography.Title>
            </div>
            <Space>
              <Button
                type="text"
                icon={<FullscreenOutlined />}
                onClick={toggleFullscreen}
                style={{ color: '#fff' }}
                title="Enter fullscreen mode (Press ESC to exit)"
              />
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <Space style={{ cursor: 'pointer', color: '#fff' }}>
                  <Avatar size="small" icon={<UserOutlined />} />
                  <Text style={{ color: '#fff' }}>{user?.full_name || user?.username}</Text>
                </Space>
              </Dropdown>
            </Space>
          </Header>
        )}
        <Content className={`monitor-content ${isFullscreen ? 'fullscreen-mode' : ''}`}>
          {children}
        </Content>
      </Layout>
    );
  }

  // Mobile mode
  if (isMobile) {
    return (
      <Layout className="dashboard-layout mobile-mode">
        <Header className="mobile-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileMenuVisible(true)}
              style={{ fontSize: '16px' }}
            />
            <Typography.Title level={5} style={{ margin: '0 0 0 12px', color: '#0066CC', fontSize: '14px' }}>
              Sales Dashboard
            </Typography.Title>
          </div>
          
          <Space>
            {onRefresh && (
              <Button 
                type="text" 
                icon={<ReloadOutlined />} 
                size="small"
                loading={refreshLoading}
                onClick={onRefresh}
                title="Refresh data"
              />
            )}
            <Badge count={3} size="small">
              <Button type="text" icon={<BellOutlined />} size="small" />
            </Badge>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#0066CC' }} />
            </Dropdown>
          </Space>
        </Header>
        
        <Content className="mobile-content">
          {children}
        </Content>
        
        <MobileMenu />
      </Layout>
    );
  }

  // Desktop mode (default)
  return (
    <Layout className={`dashboard-layout desktop-mode ${isFullscreen ? 'fullscreen-mode' : ''}`}>
      <Header 
        className={`dashboard-header ${isFullscreen ? 'fullscreen-header' : ''}`}
        style={{
          transform: isFullscreen && !headerVisible ? 'translateY(-100%)' : 'translateY(0)',
          transition: 'transform 0.3s ease-in-out',
          position: isFullscreen ? 'fixed' : 'relative',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography.Title level={4} style={{ margin: 0, color: '#0066CC', marginRight: 24 }}>
            Sales Dashboard
          </Typography.Title>
          
          {/* Navigation Menu Dropdown */}
          <Dropdown 
            menu={{ 
              items: getMenuItems().map(item => ({
                ...item,
                onClick: () => handleMenuClick({ key: item.key })
              })),
              selectedKeys: [location.pathname]
            }}
            placement="bottomLeft"
          >
            <Button 
              type="text" 
              icon={<AppstoreOutlined />}
              size="large"
              style={{ marginRight: 16 }}
            />
          </Dropdown>
        </div>
        
        <Space size="middle">
          {onRefresh && (
            <Button 
              type="text" 
              icon={<ReloadOutlined />} 
              size="large"
              loading={refreshLoading}
              onClick={onRefresh}
              title="刷新数据"
            />
          )}
          
          <Badge count={3}>
            <Button 
              type="text" 
              icon={<BellOutlined />} 
              size="large"
            />
          </Badge>
          
          {/* Show fullscreen toggle button for admin users */}
          {user?.role === 'admin' && (
            <Button
              type="text"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
              size="large"
              title={isFullscreen ? '退出全屏' : '进入全屏'}
            />
          )}
          
          <Dropdown 
            menu={{ items: userMenuItems }}
            placement="bottomRight"
          >
            <Space style={{ cursor: 'pointer' }}>
              <Avatar 
                size="small" 
                icon={<UserOutlined />} 
                style={{ backgroundColor: '#0066CC' }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text strong>{user?.full_name || user?.username}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {getRoleDisplayName(user?.role || '')}
                </Text>
              </div>
            </Space>
          </Dropdown>
        </Space>
      </Header>
      
      <Content 
        className="dashboard-content" 
        style={{ 
          margin: 0,
          paddingTop: isFullscreen && !headerVisible ? 0 : undefined
        }}
      >
        {children}
      </Content>
    </Layout>
  );
};

export default DashboardLayout;